import { defineStore } from "pinia";
import router from "@/router";

import AuthService from "@/services/authService.ts";
import localPermissions from "@/constants/localPermissions.ts";

import type {
  Collaborator,
  LoginFinalizationRequest,
  LoginRequest,
  LoginValidationResponse,
  MeResponse,
} from "@/types/services/auth.type.ts";
import type {
  AuthState,
  InitialState,
  InitialUser,
} from "@/types/stores/authStore.type.ts";

const userLocal: string | null = localStorage.getItem("userDetail");
const collaboratorLocal: string | null = localStorage.getItem(
  "currentCollaborator",
);

const initialState: InitialState = userLocal
  ? { loggedIn: true }
  : { loggedIn: false };

const initialUser: InitialUser = userLocal ? JSON.parse(userLocal) : {};
const initialCollaborator: Collaborator | null = collaboratorLocal
  ? JSON.parse(collaboratorLocal)
  : null;

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    auth: initialState,
    userDetail: initialUser,
    currentCollaborator: initialCollaborator,
  }),
  actions: {
    async loginValidation(
      userData: LoginRequest,
    ): Promise<LoginValidationResponse> {
      return AuthService.loginValidation(userData);
    },
    async loginFinalization(payload: LoginFinalizationRequest): Promise<void> {
      await AuthService.loginFinalization(payload);
      this.auth.loggedIn = true;
      await this.me();
    },
    async verifyLogin2faCode(code: string): Promise<void> {
      await AuthService.verifyLogin2faCode(code);
    },
    async resendLogin2faCode(): Promise<void> {
      await AuthService.resendLogin2faCode();
    },
    async me(): Promise<void> {
      const userDetail = await AuthService.me();
      this.meSuccess(userDetail);
    },
    async logoutAction(): Promise<void> {
      try {
        await AuthService.logout();
      } catch {
        // A network or server error must not block the logout flow —
        // local state is always cleaned via finally.
      } finally {
        this.cleanSession();
        if (router.currentRoute.value.path !== "/login") {
          await router.push("/login");
        }
      }
    },
    cleanSession(): void {
      this.auth.loggedIn = false;
      this.userDetail = {};
      this.currentCollaborator = null;
      localStorage.removeItem("userDetail");
      localStorage.removeItem("currentCollaborator");
    },
    meSuccess(userDetail: MeResponse): void {
      this.userDetail = userDetail;
      this.auth.loggedIn = true;
      localStorage.setItem("userDetail", JSON.stringify(userDetail));
    },
    selectedCollaborator(collaborator: Collaborator): void {
      this.currentCollaborator = collaborator;
      localStorage.setItem("currentCollaborator", JSON.stringify(collaborator));
    },
    updateCollaborator(collaborator: Collaborator): void {
      const userDetail = this.userDetail as MeResponse;
      userDetail.collaborators = (userDetail.collaborators ?? []).map(
        (item: Collaborator): Collaborator =>
          item.technical_id === collaborator.technical_id ? collaborator : item,
      );
      this.meSuccess(userDetail);

      if (
        this.currentCollaborator?.technical_id === collaborator.technical_id
      ) {
        this.selectedCollaborator(collaborator);
      }
    },
    hasPermission(permissionRequired: string[] = []): boolean {
      if (permissionRequired.length === 0) return true;

      const userPermissions = (this.userDetail as MeResponse).permissions;
      if (!Array.isArray(userPermissions)) return false;

      const permissionSet = new Set(
        userPermissions.map((permission) => permission.title),
      );

      return permissionRequired.some((permission): boolean => {
        if (permission in localPermissions) {
          const permissionObj =
            localPermissions[permission as keyof typeof localPermissions];
          return permissionSet.has(permissionObj.title);
        }
        return false;
      });
    },
  },
});
